'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { FileWarning } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Route-segment error boundary for `/legal/*`. Only reached when
 * `fetchPublicPolicy` (src/lib/legal-policies.ts) throws — i.e. a real
 * outage, not a 404 (that's `not-found.tsx`). Most requests never see this:
 * once a policy page has rendered successfully once, Next's ISR keeps
 * serving that last-good copy on a later revalidation failure. This is the
 * fallback for a cold cache (nothing generated yet) — same pattern as
 * `(app)/companies/error.tsx`.
 */
export default function LegalPolicyError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error('[legal-policy-unavailable]', error.digest || error.message);
  }, [error]);

  return (
    <div className="mx-auto flex min-h-[50vh] max-w-[70ch] flex-col items-center justify-center gap-3 px-4 py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
        <FileWarning aria-hidden="true" className="h-7 w-7 text-muted-foreground" />
      </div>
      <h1 className="text-2xl font-semibold text-foreground">This document is temporarily unavailable</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        We could not load the latest copy right now. Nothing has changed about the document itself —
        please try again shortly.
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        <Button onClick={reset}>Try again</Button>
        <Button asChild variant="outline">
          <Link href="/legal">All legal documents</Link>
        </Button>
      </div>
    </div>
  );
}
